import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

interface DeleteHourlyTuitionDraftParams {
  draftId: string;
}

interface DeleteHourlyTuitionDraftResponse {
  success: boolean;
  message?: string;
}

interface ApiErrorResponse {
  error: string;
}

export const deleteHourlyTuitionDraft = async ({
  draftId,
}: DeleteHourlyTuitionDraftParams): Promise<DeleteHourlyTuitionDraftResponse> => {
  try {
    const response = await baseAPI.delete<DeleteHourlyTuitionDraftResponse>(
      `/hourly-tuition-billing/draft/${draftId}`,
    );

    if (!response.data.success) {
      throw new Error(response.data.message ?? 'Failed to delete draft');
    }

    return response.data;
  } catch (error) {
    if (isAxiosError<ApiErrorResponse>(error) && error.response?.data.error) {
      throw new Error(error.response.data.error);
    }

    throw error;
  }
};

export const useDeleteHourlyTuitionDraft = () => {
  const queryClient = useQueryClient();

  return useMutation<
    DeleteHourlyTuitionDraftResponse,
    Error,
    DeleteHourlyTuitionDraftParams
  >({
    mutationFn: deleteHourlyTuitionDraft,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [KEYS.HOURLY_TUITION_BILLING, KEYS.DRAFTS],
      });
      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === KEYS.HOURLY_TUITION_BILLING &&
          query.queryKey[1] === KEYS.SESSION_BILLING_BREAKDOWN,
      });
    },
  });
};
